import { Link } from '@tanstack/react-router';
import { Brain } from 'lucide-react';
import { useState } from 'react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ROUTES } from '@/lib/route-paths';

export interface MemoryRecallBadgePayload {
  knowledgeEntryId: string;
  content: string;
  scope: 'personal' | 'organization';
}

export interface MemoryRecallBadgeProps {
  payload: MemoryRecallBadgePayload;
}

/**
 * Inline "Claire remembered…" chip rendered below an assistant message
 * when its response was informed by a `preference` knowledge entry.
 *
 * Emit side wired in Phase 3 (docs/plans/claire-memory-suggestions.md):
 * `plan-chat-turn.ts`'s `run()` emits one `data-memory_recalled` event per
 * `preference` row `queryKnowledge` judged relevant to the turn, right
 * after `start`. See `message-bucketing.ts`'s `asMemoryRecallPart` for the
 * part-type recognition that drives this.
 *
 * Tone: small ghost-styled chip, Irish-warm copy ("Claire remembered…"),
 * doesn't dominate the message body.
 */
export function MemoryRecallBadge({ payload }: MemoryRecallBadgeProps) {
  const [open, setOpen] = useState(false);

  // Trim long memories for the chip; the dialog shows the full text.
  const previewLength = 60;
  const preview =
    payload.content.length > previewLength
      ? `${payload.content.slice(0, previewLength).trim()}…`
      : payload.content;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="inline-flex w-fit items-center gap-1.5 text-left"
        aria-label="View memory Claire used in this answer"
      >
        <Badge
          variant="outline"
          className="text-muted-foreground hover:bg-muted gap-1.5 font-normal"
        >
          <Brain className="size-3" />
          <span className="font-medium">Claire remembered:</span>
          <span className="truncate">{preview}</span>
        </Badge>
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Claire remembered this</DialogTitle>
            <DialogDescription>
              {payload.scope === 'personal'
                ? 'Stored just for you. You can edit or remove it any time.'
                : 'Stored for the whole team. Anyone in the org sees it.'}
            </DialogDescription>
          </DialogHeader>

          <p className="whitespace-pre-wrap rounded-md bg-muted p-3 text-sm">
            {payload.content}
          </p>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>
              Close
            </Button>
            {/* Memories live under Settings > Organisation > Claire memories,
                a real route — so the deep link works and lands on the row. */}
            <Button asChild>
              <Link to={ROUTES.settingsClaireMemories}>Manage memories</Link>
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
